// Patches the H.264 level_idc field inside an MP4 to a value iOS Safari
// will accept, without re-encoding any frame data. Equivalent to running
//   ffmpeg -i in.mp4 -c copy -bsf:v h264_metadata=level=4 out.mp4
// in JavaScript on the bytes of a downloaded blob.
//
// Why this exists: some encoder pipelines (FFmpeg invoked without `-level`)
// stamp H.264 level 6.2 (the spec ceiling, "8K @ 120 fps") even on small
// 720p25 clips. iOS 18.7+ Safari's stricter media decoder reads level_idc
// from the SPS at probe time and rejects anything > level 5.1 with
// MEDIA_ERR_SRC_NOT_SUPPORTED, regardless of actual content size.
//
// The level lives in two places inside the MP4:
//   1. AVCDecoderConfigurationRecord.AVCLevelIndication, the 4th byte of
//      the avcC box payload.
//   2. The level_idc field of each embedded SPS NAL unit (3 bytes after
//      the NAL header byte).
// We patch both so any decoder lookup path sees the corrected value.
// Coded slice (frame) data is never touched — bit-for-bit identical pixels.

// 51 = level 5.1. iOS Safari's strictest current threshold (some devices
// accept 5.2 leniently, but 5.1 is universally safe). Above this we patch.
const MAX_SAFE_LEVEL = 51;

// 40 = level 4.0. Covers up to 1080p30 at ~25 Mbps, accepted on every
// iPhone since iPhone 5s. Plenty for any assessment video we serve.
const TARGET_LEVEL = 40;

// Search for the start of an avcC box payload. Returns the offset of the
// configurationVersion byte, or -1 if not found. Two false-positive guards:
//   1. configurationVersion must equal 1 for a valid AVCDecoderConfigurationRecord.
//   2. If the box has a preceding 4-byte size header (i.e. we're at offset >= 4),
//      the size must fall in a plausible range (16..16384 bytes — real avcC
//      boxes are typically 30-100 bytes, never beyond a few KB).
// Combined, these reduce false positives in random mdat bytes to ~1 in 10^14.
function findAvcCPayload(u8, fromIndex = 0) {
    for (let i = fromIndex; i < u8.length - 16; i++) {
        if (
            u8[i] === 0x61 && // 'a'
            u8[i + 1] === 0x76 && // 'v'
            u8[i + 2] === 0x63 && // 'c'
            u8[i + 3] === 0x43 && // 'C'
            u8[i + 4] === 0x01 // configurationVersion
        ) {
            if (i >= 4) {
                const boxSize =
                    (u8[i - 4] << 24) |
                    (u8[i - 3] << 16) |
                    (u8[i - 2] << 8) |
                    u8[i - 1];
                if (boxSize < 16 || boxSize > 16384) continue;
            }
            return i + 4;
        }
    }
    return -1;
}

// Returns the AVCLevelIndication byte from the first avcC box found, or
// null if no avcC is present (HEVC, non-MP4, corrupt buffer, etc.).
export function readH264Level(buffer) {
    const u8 = new Uint8Array(buffer);
    const start = findAvcCPayload(u8);
    if (start < 0) return null;
    return u8[start + 3];
}

// MUTATES `buffer`: walks every avcC box and embedded SPS NAL and writes
// the given level into the level_idc bytes. Returns the same underlying
// ArrayBuffer (not a copy) for chaining. Safe to call on buffers without
// avcC (no-op). The caller should not retain the original buffer expecting
// it to be unchanged.
export function patchH264Level(buffer, targetLevel = TARGET_LEVEL) {
    const u8 = new Uint8Array(buffer);
    let cursor = 0;
    while (cursor < u8.length - 16) {
        const start = findAvcCPayload(u8, cursor);
        if (start < 0) break;

        // Patch AVCLevelIndication in the avcC box header.
        u8[start + 3] = targetLevel;

        // Walk the embedded SPS NAL units (the avcC box also stores them
        // inline so the decoder can initialize without reading a frame).
        // numOfSequenceParameterSets is the lower 5 bits of byte at start+5.
        const numSPS = u8[start + 5] & 0x1f;
        let off = start + 6;
        for (let s = 0; s < numSPS; s++) {
            if (off + 2 > u8.length) break;
            const spsLen = (u8[off] << 8) | u8[off + 1];
            off += 2;
            if (off + spsLen > u8.length) break;
            // SPS NAL layout: byte 0 = NAL header, byte 1 = profile_idc,
            // byte 2 = constraint flags + reserved bits, byte 3 = level_idc.
            if (spsLen >= 4) u8[off + 3] = targetLevel;
            off += spsLen;
        }
        cursor = off > start ? off : start + 1;
    }
    return u8.buffer;
}

// High-level entry point: reads the level, patches if too high, returns
// a result object with the (possibly mutated) buffer plus diagnostics.
// The diagnostics let the caller log when a patch was applied so we can
// monitor which videos in production needed the fix.
export function maybePatchH264Level(buffer) {
    const level = readH264Level(buffer);
    if (level == null || level <= MAX_SAFE_LEVEL) {
        return { buffer, patched: false, level, newLevel: null };
    }
    const patched = patchH264Level(buffer, TARGET_LEVEL);
    return { buffer: patched, patched: true, level, newLevel: TARGET_LEVEL };
}
